import { useEffect, useRef, lazy, Suspense } from 'react';

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ConsentBanner } from './components/ConsentBanner';
import { RateEntryLoading } from './components/RateEntryLoading';
import { RouteMetadata } from './components/RouteMetadata';
import { ScrollToTop } from './components/ScrollToTop';
import { ApplyModalProvider, useApplyModal } from './contexts/ApplyModalContext';
// Leaf module with zero imports — importing it from the feature index would
// drag all of Orion into the entry chunk.
import { isSupabaseAuthCallback } from './features/orion/authCallback';
import { parseRateEntry } from './features/rate-experience/rateEntry';
import { enforceAnalyticsRouteBoundary, trackPageView } from './lib/analytics/gtm';
import { isRateQuotePath, isRouteBrowsable } from './lib/analytics/publicRoutes';
import { CONSUMER_RATE_EXPERIENCE_AVAILABLE } from './lib/consumerRateAvailability';
import { completeVitePreloadRecovery } from './utils/vitePreloadRecovery';

// Every route is code-split so no page pays for another page's dependencies
// (recharts, swiper, pdf.js, supabase all live in lazy chunks). Pages use
// named exports, hence the explicit adapters.
const ConsumerLanding = lazy(() => import('./pages/ConsumerLanding').then(m => ({ default: m.ConsumerLanding })));
const RecruitLO = lazy(() => import('./pages/RecruitLO').then(m => ({ default: m.RecruitLO })));
const JoinMarketing = lazy(() => import('./pages/JoinMarketing').then(m => ({ default: m.JoinMarketing })));
const ResourcesPage = lazy(() => import('./pages/Resources').then(m => ({ default: m.ResourcesPage })));
const MortgagePreApprovalPage = lazy(() => import('./pages/MortgagePreApprovalPage').then(m => ({ default: m.MortgagePreApprovalPage })));
const LoanOfficers = lazy(() => import('./pages/LoanOfficers').then(m => ({ default: m.LoanOfficers })));
const CalculatorsPage = lazy(() => import('./pages/CalculatorsPage').then(m => ({ default: m.CalculatorsPage })));
const AffordabilityCalculatorPage = lazy(() => import('./pages/AffordabilityCalculatorPage').then(m => ({ default: m.AffordabilityCalculatorPage })));
const RefinanceCalculatorPage = lazy(() => import('./pages/RefinanceCalculatorPage').then(m => ({ default: m.RefinanceCalculatorPage })));
const BuydownCalculatorPage = lazy(() => import('./pages/BuydownCalculatorPage').then(m => ({ default: m.BuydownCalculatorPage })));
const PointsCalculatorPage = lazy(() => import('./pages/PointsCalculatorPage').then(m => ({ default: m.PointsCalculatorPage })));
const DscrCalculatorPage = lazy(() => import('./pages/DscrCalculatorPage').then(m => ({ default: m.DscrCalculatorPage })));
const TechnologyPage = lazy(() => import('./pages/Technology').then(m => ({ default: m.TechnologyPage })));
const AboutPage = lazy(() => import('./pages/About').then(m => ({ default: m.AboutPage })));
const BuyGuidePage = lazy(() => import('./pages/BuyGuidePage').then(m => ({ default: m.BuyGuidePage })));
const FirstTimeHomeBuyers = lazy(() => import('./pages/FirstTimeHomeBuyers').then(m => ({ default: m.FirstTimeHomeBuyers })));
const ConventionalLoans = lazy(() => import('./pages/ConventionalLoans').then(m => ({ default: m.ConventionalLoans })));
const FhaLoans = lazy(() => import('./pages/FhaLoans').then(m => ({ default: m.FhaLoans })));
const FhaVsConventional = lazy(() => import('./pages/FhaVsConventional').then(m => ({ default: m.FhaVsConventional })));
const NonQmLoans = lazy(() => import('./pages/NonQmLoans').then(m => ({ default: m.NonQmLoans })));
const BankStatementLoans = lazy(() => import('./pages/BankStatementLoans').then(m => ({ default: m.BankStatementLoans })));
const DscrLoans = lazy(() => import('./pages/DscrLoans').then(m => ({ default: m.DscrLoans })));
const DscrRatesPage = lazy(() => import('./pages/DscrRatesPage').then(m => ({ default: m.DscrRatesPage })));
const LearnIndexPage = lazy(() => import('./pages/LearnIndexPage').then(m => ({ default: m.LearnIndexPage })));
const LearnArticlePage = lazy(() => import('./pages/LearnArticlePage').then(m => ({ default: m.LearnArticlePage })));
const LearnDscrCalculatorArticlePage = lazy(() => import('./pages/LearnDscrCalculatorArticlePage').then(m => ({ default: m.LearnDscrCalculatorArticlePage })));
const RefinanceGuide = lazy(() => import('./pages/RefinanceGuide').then(m => ({ default: m.RefinanceGuide })));
const RefinanceLoans = lazy(() => import('./pages/RefinanceLoans').then(m => ({ default: m.RefinanceLoans })));
const MortgageRatesPage = lazy(() => import('./pages/MortgageRatesPage').then(m => ({ default: m.MortgageRatesPage })));
const MortgageInterestRates = lazy(() => import('./pages/MortgageInterestRates').then(m => ({ default: m.MortgageInterestRates })));
const MortgageNews = lazy(() => import('./pages/MortgageNews').then(m => ({ default: m.MortgageNews })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
// Officer profile pages: one lazy chunk carries the template registry and its
// fonts, so only profile visitors ever download them.
const LoanOfficerProfilePage = lazy(() => import('./pages/LoanOfficerProfilePage').then(m => ({ default: m.LoanOfficerProfilePage })));
const RecruitLoginRoute = lazy(() => import('./features/onboarding/RecruitLoginRoute'));
const OrionApp = lazy(() => import('./features/orion'));
// LO-authored private quote delivery. Token-gated, noindex, analytics-
// ineligible; its chunk (splash canvas + card layout) loads only for a
// borrower who opened a real link.
const RateQuotePage = lazy(() => import('./features/rate-quote'));
const RateQuotePreviewPage = lazy(() => import('./features/rate-quote').then(m => ({ default: m.RateQuotePreviewPage })));
const RateWatchCancellation = lazy(() => import('./features/rate-watch-cancellation/RateWatchCancellation'));
// Compile-time gated prototype. In a normal production build this branch is
// folded away, including its dynamic import and every synthetic fixture.
const RateExperiencePage =
  CONSUMER_RATE_EXPERIENCE_AVAILABLE
    ? lazy(() => import('./features/rate-experience/RateExperiencePage').then(m => ({ default: m.RateExperiencePage })))
    : null;
// Deferred with its Supabase-backed service; mounted only while open.
const ApplyNowModal = lazy(() => import('./components/ApplyNowModal').then(m => ({ default: m.ApplyNowModal })));

// The static compliance document (public/privacy-policy.html) is CANONICAL:
// direct requests already rewrite to it via netlify.toml, and client-side
// navigation must not render a second, drifting React copy. Targeting the
// .html file explicitly works on Netlify AND under vite preview (which has no
// redirect engine — a bare /privacy-policy would loop back into the SPA).
function StaticPrivacyPolicy() {
  useEffect(() => {
    window.location.replace('/privacy-policy.html');
  }, []);
  return <div className="min-h-screen bg-brand-dark-green" />;
}

// Static routes emit manual, sanitized page views here. Officer pages wait
// for a published profile in LoanOfficerProfilePage before using the same
// trackPageView gateway. GA4's automatic page_view/history tracking stays off.
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (
      enforceAnalyticsRouteBoundary(
        location.pathname,
        location.search,
        location.hash
      )
    ) {
      return;
    }
    trackPageView(location.pathname);
  }, [location.hash, location.pathname, location.search]);
  return null;
}

function RateExperienceRoute() {
  const { search } = useLocation();
  // Stopping an existing subscription remains possible even when acquisition
  // and the entire personalized feature are compiled out of production.
  if (new URLSearchParams(search).get('view') === 'unsubscribe') return <RateWatchCancellation />;
  if (!RateExperiencePage) return <Navigate to="/" replace />;
  return <RateExperiencePage />;
}

// This component is deliberately inside the same Suspense boundary as the
// lazy route. Its effect cannot run until the matched route module resolves
// and React commits the hydrated route, giving production tests an explicit
// completion signal instead of guessing from timers or third-party network
// idleness.
function HydrationCommitMarker() {
  useEffect(() => {
    completeVitePreloadRecovery();
    const root = document.getElementById('root');
    if (root?.dataset.lwPrerendered === 'true') {
      document.documentElement.dataset.lwHydrated = 'true';
    }
  }, []);

  return null;
}

// Routes that own long-lived state — an auth session or a multi-step wizard —
// must survive navigation without being torn down by the marketing-page reset.
const STATEFUL_ROUTES = ['/orion', '/recruit-login', '/rate-experience', '/rate-quote'];

// Marketing pages should reopen at the top and replay their entrance state,
// including when a visitor follows a new history entry to the same pathname.
// Hash-only navigation keeps the current page mounted so in-page anchors and
// the loan-officer directory's deep links retain control of scrolling.
function useVisitKey() {
  const { pathname, key, hash } = useLocation();
  const visitKey = useRef(key);
  const previousPathname = useRef(pathname);

  const hashOnlyChange = hash !== '' && pathname === previousPathname.current;
  if (!hashOnlyChange) visitKey.current = key;
  previousPathname.current = pathname;

  if (STATEFUL_ROUTES.some((route) => pathname.startsWith(route))) return 'stateful';
  return visitKey.current;
}

// Separate component to use the modal context
function AppContent() {
  const { isOpen, closeModal } = useApplyModal();

  const location = useLocation();
  const navigate = useNavigate();
  const visitKey = useVisitKey();
  const rateEntry = CONSUMER_RATE_EXPERIENCE_AVAILABLE
    && location.pathname === '/rate-experience'
    && new URLSearchParams(location.search).get('view') !== 'unsubscribe'
    // This chooses only the loading screen. The lazy private page validates
    // all DSCR entry fields before using them.
    && (parseRateEntry(new URLSearchParams(location.search)) || new URLSearchParams(location.search).get('entry') === 'dscr');

  // Orion admin sign-in is the only Supabase OAuth flow in this app, so any OAuth
  // callback that lands anywhere other than /orion (e.g. the Supabase Site-URL
  // fallback) belongs to Orion. Route it back, preserving the token-bearing
  // query/hash, keyed strictly on real callback params (never the bare pathname)
  // so ordinary page loads are never redirected.
  useEffect(() => {
    if (location.pathname === '/orion') return;
    if (!isSupabaseAuthCallback(location.search, location.hash)) return;
    navigate(`/orion${location.search}${location.hash}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  return (
    <>
      <ScrollToTop />
      <RouteTracker />
      <RouteMetadata />
      <ConsentBanner />
      <div className="min-h-screen">
        {/* Solid route-matched fallback: chunks are small, a spinner would
            flash. Quote routes paint Letterpress paper end to end (the shell
            already painted paper, so a green fallback here reintroduces the
            exact flash the shell fix removed); every other route keeps the
            brand green. */}
        <Suspense fallback={rateEntry ? <RateEntryLoading /> :
          <div className={`min-h-screen ${
            location.pathname === '/rate-quote/preview' || isRateQuotePath(location.pathname)
              ? 'bg-brand-paper'
              : 'bg-brand-dark-green'
          }`} />
        }>
          <Routes key={visitKey}>
            <Route path="/buy/first-time" element={<FirstTimeHomeBuyers />} />
            <Route path="/buy/pre-approval" element={<MortgagePreApprovalPage />} />
            <Route path="/buy/guide" element={<BuyGuidePage />} />
            <Route path="/loans/conventional" element={<ConventionalLoans />} />
            <Route path="/loans/fha" element={<FhaLoans />} />
            <Route path="/loans/fha-vs-conventional" element={<FhaVsConventional />} />
            {/* Publication-aware specialty routes. A future held route drops
                from client routing, navigation, prerendering, and the sitemap
                through the same registry flag while remaining reviewable in dev. */}
            {isRouteBrowsable('/loans/non-qm') ? (
              <Route path="/loans/non-qm" element={<NonQmLoans />} />
            ) : null}
            {isRouteBrowsable('/loans/bank-statement') ? (
              <Route path="/loans/bank-statement" element={<BankStatementLoans />} />
            ) : null}
            {isRouteBrowsable('/loans/dscr') ? (
              <Route path="/loans/dscr" element={<DscrLoans />} />
            ) : null}
            <Route path="/refinance/guide" element={<RefinanceGuide />} />
            <Route path="/refinance/loans" element={<RefinanceLoans />} />
            <Route path="/mortgage-rates" element={<MortgageRatesPage />} />
            {isRouteBrowsable('/mortgage-rates/dscr') && <Route path="/mortgage-rates/dscr" element={<DscrRatesPage />} />}
            {isRouteBrowsable('/mortgage-rates/dscr/california') && <Route path="/mortgage-rates/dscr/california" element={<DscrRatesPage />} />}
            {isRouteBrowsable('/mortgage-rates/dscr/ohio') && <Route path="/mortgage-rates/dscr/ohio" element={<DscrRatesPage />} />}
            {isRouteBrowsable('/mortgage-rates/dscr/florida') && <Route path="/mortgage-rates/dscr/florida" element={<DscrRatesPage />} />}
            {isRouteBrowsable('/mortgage-rates/dscr/texas') && <Route path="/mortgage-rates/dscr/texas" element={<DscrRatesPage />} />}
            {isRouteBrowsable('/mortgage-rates/dscr/tennessee') && <Route path="/mortgage-rates/dscr/tennessee" element={<DscrRatesPage />} />}
            <Route path="/resources/mortgage-rates" element={<MortgageInterestRates />} />
            <Route path="/resources/mortgage-news" element={<MortgageNews />} />
            {/* Learn library: held until the cohort gate opens; the dedicated calculator article route precedes the dynamic one. */}
            {isRouteBrowsable('/learn') && <Route path="/learn" element={<LearnIndexPage />} />}
            {isRouteBrowsable('/learn/what-is-a-good-dscr') && <Route path="/learn/what-is-a-good-dscr" element={<LearnDscrCalculatorArticlePage />} />}
            {isRouteBrowsable('/learn') && <Route path="/learn/:slug" element={<LearnArticlePage />} />}
            {/* Redirects for backward compatibility */}
            <Route path="/buy/rates" element={<Navigate to="/mortgage-rates" replace />} />
            <Route path="/refinance/rates" element={<Navigate to="/resources/mortgage-rates" replace />} />
            <Route path="/apply" element={<Navigate to="/" replace />} />
            <Route path="/orion" element={<OrionApp />} />
            <Route path="/recruit-login" element={<RecruitLoginRoute />} />
            <Route path="/rate-experience" element={<RateExperienceRoute />} />
            <Route path="/rate-quote/preview" element={<RateQuotePreviewPage />} />
            <Route path="/rate-quote/:token" element={<RateQuotePage />} />
            {/* RecruitLO renders its own Footer (as every page does) */}
            <Route path="/join" element={<RecruitLO />} />
            <Route path="/join/marketing" element={<JoinMarketing />} />
            {/* Legacy recruiting URL — keep old links and bookmarks working */}
            <Route path="/recruit-lo" element={<Navigate to="/join" replace />} />
            <Route path="/" element={<ConsumerLanding />} />
            <Route path="/loan-officers" element={<LoanOfficers />} />
            <Route path="/loan-officers/:slug" element={<LoanOfficerProfilePage />} />
            <Route path="/calculators" element={<CalculatorsPage />} />
            {isRouteBrowsable('/calculators/affordability') && <Route path="/calculators/affordability" element={<AffordabilityCalculatorPage />} />}
            {isRouteBrowsable('/calculators/refinance') && <Route path="/calculators/refinance" element={<RefinanceCalculatorPage />} />}
            {isRouteBrowsable('/calculators/buydown') && <Route path="/calculators/buydown" element={<BuydownCalculatorPage />} />}
            {isRouteBrowsable('/calculators/discount-points') && <Route path="/calculators/discount-points" element={<PointsCalculatorPage />} />}
            {isRouteBrowsable('/calculators/dscr') && <Route path="/calculators/dscr" element={<DscrCalculatorPage />} />}
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/technology" element={<TechnologyPage />} />
            <Route path="/why-lendwise" element={<AboutPage />} />
            {/* Legacy path — keep old /about links and bookmarks working */}
            <Route path="/about" element={<Navigate to="/why-lendwise" replace />} />
            <Route path="/privacy-policy" element={<StaticPrivacyPolicy />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <HydrationCommitMarker />
        </Suspense>
      </div>
      {/* Single modal instance for the entire app. Rendered only while open —
          its own boundary because the Routes' Suspense does not cover this
          sibling — so its chunk (and the Supabase client it pulls in) loads on
          first open, not at startup. */}
      {isOpen && (
        <Suspense fallback={null}>
          <ApplyNowModal isOpen={isOpen} onClose={closeModal} />
        </Suspense>
      )}
    </>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ApplyModalProvider>
        <AppContent />
      </ApplyModalProvider>
    </Router>
  );
}

export default App
